import React, { useEffect } from 'react';
import { CheckCircle, Calendar, Clock } from 'lucide-react';
import Footer from '../components/layout/Footer';

const PostBooking = () => {
  useEffect(() => {
    document.title = "Call Confirmed | Order Flow School";
    window.scrollTo({ top: 0, behavior: 'instant' });
    console.log('[Analytics] Post-booking confirmation page viewed');
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#000000' }}>
      {/* Nav */}
      <nav className="g-nav" style={{ background: 'rgba(0,0,0,0.85)' }}>
        <a href="/" className="flex flex-col leading-none" style={{ textDecoration: 'none' }}>
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 26, color: '#C9A235' }}>OFS</span>
          <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 7, letterSpacing: '0.25em', color: 'rgba(201,162,53,0.6)', textTransform: 'uppercase', marginTop: -1 }}>ORDER FLOW SCHOOL</span>
        </a>
      </nav>

      <main style={{ paddingTop: 100, paddingBottom: 40 }}>
        {/* ═══ SECTION A — Confirmation Hero ═══ */}
        <section style={{ padding: '60px 20px' }}>
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="flex justify-center">
              <div className="flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,217,126,0.12)', border: '1px solid rgba(0,217,126,0.3)' }}>
                <CheckCircle style={{ width: 32, height: 32, color: '#00D97E' }} />
              </div>
            </div>
            <h1 style={{ fontFamily: "'League Spartan',sans-serif", fontSize: 'clamp(32px,5vw,48px)', color: '#FFFFFF', letterSpacing: '-0.01em' }}>
              YOUR CALL IS CONFIRMED
            </h1>
          </div>
        </section>

        <hr className="g-div" />

        {/* ═══ SECTION B — Step 1: Watch Video ═══ */}
        <section style={{ background: '#080808', padding: '60px 20px' }}>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-4">
              <span className="g-badge">Step 1 — Required</span>
              <h2 style={{ fontFamily: "'League Spartan',sans-serif", fontSize: 'clamp(28px,4vw,40px)', color: '#FFFFFF', letterSpacing: '-0.01em', marginTop: 12 }}>
                WATCH THIS BEFORE YOUR CALL
              </h2>
            </div>

            {/* Warning */}
            <div style={{ background: 'rgba(198,63,92,0.08)', borderLeft: '3px solid #C63F5C', borderRadius: 4, padding: '14px 20px', maxWidth: 700, margin: '0 auto' }} data-testid="urgency-banner">
              <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 14, color: '#F0F4FA' }}>
                Showing up without watching this video will count as a missed call. Your spot will be given to the next applicant.
              </p>
            </div>

            {/* Video */}
            <div style={{ border: '1px solid rgba(201,162,53,0.2)', borderRadius: 4, overflow: 'hidden', maxWidth: 800, margin: '0 auto', width: '100%' }}>
              <iframe
                src="https://player.vimeo.com/video/1175990550?badge=0&autopause=0&player_id=0"
                className="w-full aspect-video"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                referrerPolicy="origin"
                allowFullScreen
                title="Pre-Call Message from the Founder"
                data-testid="pre-call-video"
              />
            </div>

            <div className="flex items-center justify-center gap-2" style={{ color: '#8D97A8', fontSize: 14 }}>
              <Clock style={{ width: 16, height: 16 }} />
              <span style={{ fontFamily: "'Inter',sans-serif" }}>Watch the full video — it takes less than 5 minutes</span>
            </div>
          </div>
        </section>

        <hr className="g-div" />

        {/* ═══ SECTION C — Step 2: Confirm Call ═══ */}
        <section style={{ padding: '60px 20px' }}>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center space-y-4">
              <span className="g-badge">Step 2 — Confirm</span>
              <h2 style={{ fontFamily: "'League Spartan',sans-serif", fontSize: 'clamp(28px,4vw,40px)', color: '#FFFFFF', letterSpacing: '-0.01em', marginTop: 12 }}>
                CONFIRM YOUR CALL
              </h2>
            </div>

            <div className="g-card p-8 space-y-6">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="flex items-center justify-center" style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(201,162,53,0.12)', border: '1px solid rgba(201,162,53,0.25)' }}>
                    <Calendar style={{ width: 28, height: 28, color: '#C9A235' }} />
                  </div>
                </div>
                <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 20, color: '#F0F4FA' }}>
                  Check your email for your call details
                </p>
                <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 400, fontSize: 15, color: '#8D97A8' }}>
                  Save the time and add it to your calendar. You'll receive a reminder 24 hours before your call.
                </p>
              </div>

              <hr style={{ border: 'none', height: 1, background: 'rgba(201,162,53,0.15)' }} />

              <div className="text-center space-y-3">
                <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 15, color: '#F0F4FA', maxWidth: 560, margin: '0 auto' }}>
                  Only keep this booking if you are serious about improving your trading and committed to showing up.
                </p>
                <p style={{ fontFamily: "'Inter',sans-serif", fontWeight: 400, fontSize: 14, color: '#C63F5C' }}>
                  Missed calls result in removal from future admissions consideration.
                </p>
              </div>
            </div>
          </div>
        </section>

        <hr className="g-div" />

        {/* ═══ SECTION D — Before Your Call ═══ */}
        <section style={{ background: '#080808', borderTop: '1px solid rgba(201,162,53,0.12)', borderBottom: '1px solid rgba(201,162,53,0.12)', padding: '48px 24px' }}>
          <div className="max-w-4xl mx-auto">
            <h3 className="text-center" style={{ fontFamily: "'League Spartan',sans-serif", fontSize: 'clamp(26px,3.5vw,36px)', color: '#FFFFFF', letterSpacing: '-0.01em', marginBottom: 8 }}>
              BEFORE YOUR CALL
            </h3>
            <p className="text-center" style={{ fontFamily: "'Inter',sans-serif", fontWeight: 400, fontSize: 15, color: '#8D97A8', maxWidth: 560, margin: '0 auto 40px' }}>
              This is not a generic sales chat. It's a serious conversation to see whether Order Flow School is the right fit for your goals, experience, and commitment.
            </p>

            <div style={{ background: '#0C0C0C', border: '1px solid rgba(201,162,53,0.12)', borderTop: '2px solid #C9A235', borderRadius: 4, padding: '28px 32px', maxWidth: 640, margin: '0 auto' }}>
              {[
                'Watch the video above in full',
                'Show up on time',
                'Be somewhere quiet',
                'Be ready to talk honestly about your trading',
                'Be ready to make a decision if it feels like a fit',
              ].map((item, i, arr) => (
                <div key={i} className="flex items-center" style={{ padding: '12px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: '50%', background: '#C9A235', color: '#1A0F00', marginRight: 16 }}>
                    <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 13 }}>{i + 1}</span>
                  </div>
                  <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 400, fontSize: 15, color: '#C8C8C8', lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>

            <div className="text-center" style={{ marginTop: 32 }}>
              <p style={{ fontFamily: "'Inter',sans-serif", fontSize: 14, color: '#8D97A8' }}>
                Questions before your call? DM us on Instagram &rarr;{' '}
                <a href="https://www.instagram.com/orderflowschool" target="_blank" rel="noopener noreferrer" style={{ color: '#C9A235', textDecoration: 'none' }}>@orderflowschool</a>
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PostBooking;
